/** Offline sync engine: push queued operations, then pull a fresh route snapshot (MOB-011 … MOB-014). */
import { api, ApiError, deviceId, OfflineError } from '../shared/api.ts';
import { idb, purgeOfflineData, purgePaper, type OutboxOp } from './store.ts';

export interface SyncSummary {
  pushed: number;
  held: number;
  rejected: number;
  papers: number;
  removed: number;
  documentsCached: number;
  wiped: boolean;
  at: string;
}

let running: Promise<SyncSummary> | null = null;

export function syncNow(): Promise<SyncSummary> {
  if (!running) running = doSync().finally(() => (running = null));
  return running;
}

async function doSync(): Promise<SyncSummary> {
  const summary: SyncSummary = { pushed: 0, held: 0, rejected: 0, papers: 0, removed: 0, documentsCached: 0, wiped: false, at: new Date().toISOString() };
  // 1. Push pending operations (idempotent by clientId — safe to retry).
  const outbox = (await idb.all<OutboxOp>('outbox')).filter((o) => o.state === 'pending');
  if (outbox.length) {
    const res = await api.post<{ results: { clientId: string; result: string; reason?: string }[] }>('/api/sync/push', {
      ops: outbox.map(({ clientId, type, paperId, baseVersion, payload }) => ({ clientId, type, paperId, baseVersion, payload })),
    });
    for (const r of res.results) {
      const op = outbox.find((o) => o.clientId === r.clientId)!;
      if (r.result === 'applied' || r.result === 'duplicate') {
        await idb.del('outbox', r.clientId);
        summary.pushed++;
      } else {
        await idb.put('outbox', { ...op, state: r.result === 'held' ? 'held' : 'rejected', message: r.reason });
        if (r.result === 'held') summary.held++;
        else summary.rejected++;
      }
    }
  }
  // 2. Pull the current route snapshot.
  const pull = await api.post<any>('/api/sync/pull');
  if (pull.wipe) {
    await purgeOfflineData();
    summary.wiped = true;
    return summary;
  }
  const keep = new Set<string>(pull.papers.map((p: any) => p.id));
  for (const existing of await idb.all<any>('papers')) {
    if (!keep.has(existing.id)) {
      await purgePaper(existing.id, existing.documents?.map((d: any) => d.id) ?? []);
      summary.removed++;
    }
  }
  for (const id of pull.removedPaperIds as string[]) await purgePaper(id);
  for (const p of pull.papers) await idb.put('papers', p);
  await idb.put('meta', pull.rules, 'rules');
  await idb.put('meta', pull.pickups ?? [], 'pickups');
  summary.papers = pull.papers.length;
  // 3. Cache documents for offline viewing (controlled storage only).
  for (const p of pull.papers) {
    for (const d of p.documents ?? []) {
      if (await idb.get('docs', d.id)) continue;
      try {
        const res = await fetch(`/api/documents/${d.id}/content`, { credentials: 'same-origin', headers: { 'X-Device-Id': deviceId() } });
        if (res.ok) {
          await idb.put('docs', { id: d.id, type: d.content_type, blob: await res.blob() });
          summary.documentsCached++;
        }
      } catch {
        /* offline mid-sync: try next time */
      }
    }
  }
  await idb.put('meta', summary, 'lastSync');
  return summary;
}

export async function queueAttempt(paperId: string, baseVersion: number, payload: Record<string, unknown>): Promise<OutboxOp> {
  const op: OutboxOp = { clientId: payload.clientId as string, type: 'attempt', paperId, baseVersion, payload, queuedAt: new Date().toISOString(), state: 'pending' };
  await idb.put('outbox', op);
  return op;
}

/** Try the attempt online for immediate validation; queue it if there is no connection. */
export async function submitAttempt(paperId: string, baseVersion: number, payload: Record<string, unknown>): Promise<{ mode: 'online' | 'queued'; result?: any }> {
  try {
    const result = await api.post(`/api/papers/${paperId}/attempts`, payload);
    syncNow().catch(() => undefined);
    return { mode: 'online', result };
  } catch (e) {
    if (e instanceof OfflineError) {
      await queueAttempt(paperId, baseVersion, payload);
      return { mode: 'queued' };
    }
    if (e instanceof ApiError && e.status >= 500) {
      await queueAttempt(paperId, baseVersion, payload);
      return { mode: 'queued' };
    }
    throw e;
  }
}
